import { create } from 'zustand';
import { logger } from '@common-ui-library/shared';
import { 
  UserManagementState, 
  UserManagementEntityType, 
  UserManagementEntity,
  ApiResponse,
  DropdownOption,
} from '../../Utils/types';
import { UserManagementService } from '../Services/UserManagementService';
import { extractErrorMessage, filterProfessionChoices, filterTimezoneChoices } from '../../Utils/utils';

export interface DropdownOptions {
  [key: string]: DropdownOption[];
}

type ApiError = { 
  response?: { status?: number }; 
  errors?: string; 
};

interface QueryState {
  search: string | null;
  filters: string[] | null;
  ordering: string | null;
}

/** Parameters for fetchEntities - matches service method signature */
interface FetchEntitiesParams {
  search?: string | null;
  filters?: string[] | null;
  ordering?: string | null;
  page?: number;
  rowsPerPage?: number;
  entityId?: string;
}

interface UserManagementStore extends UserManagementState {
  // Additional State
  queryState: QueryState;
  dropdownOptions: DropdownOptions | null;
  fetchError: { status: boolean; message: string };

  // Setters
  setState: (state: Partial<UserManagementState>) => void;
  setQueryState: (state: Partial<QueryState>) => void;
  setDropdownOptions: (options: DropdownOptions) => void;
  setCreateUpdateError: (error: { status: boolean; message: string }) => void;
  setFetchError: (error: { status: boolean; message: string }) => void;
  
  // Actions
  setIsAddEntityOpen: (isOpen: boolean) => void;
  getDropdownOptions: (service: UserManagementService, entity: UserManagementEntityType) => Promise<DropdownOptions>;
  fetchEntities: (service: UserManagementService, entity: UserManagementEntityType, params?: FetchEntitiesParams) => Promise<ApiResponse<UserManagementEntity>>;
  saveEntity: (service: UserManagementService, entity: UserManagementEntityType, item: Record<string, unknown>, create?: boolean) => Promise<{ success: boolean; data?: UserManagementEntity }>;
  deleteEntity: (service: UserManagementService, entity: UserManagementEntityType, id: string) => Promise<boolean>;
  resetStore: () => void;
}

const initialState: UserManagementState = {
  userManagementEntity: '' as UserManagementEntityType, // Will be set by consuming component
  items: [],
  selectedItem: null,
  isAddEntityOpen: false,
  pagination: undefined,
  isLoading: false,
  createUpdateError: { status: false, message: '' },
};

const initialQueryState: QueryState = {
  search: null,
  filters: null,
  ordering: null,
};

export const useUserManagementStore = create<UserManagementStore>((set, get) => ({
  ...initialState,
  queryState: { ...initialQueryState },
  dropdownOptions: null,
  fetchError: { status: false, message: '' },

  setState: (newState) => set((state) => ({ ...state, ...newState })),
  setQueryState: (newState) => set((state) => ({ queryState: { ...state.queryState, ...newState } })),
  setDropdownOptions: (options) => {
    const filtered: DropdownOptions = { ...options };
    
    // Only filter and add properties if they exist in the input
    if (options.profession_choices !== undefined) {
      filtered.profession_choices = filterProfessionChoices(options.profession_choices);
    }
    if (options.timezone_choices !== undefined) {
      filtered.timezone_choices = filterTimezoneChoices(options.timezone_choices);
    }
    
    set({ dropdownOptions: filtered });
  },
  setCreateUpdateError: (error) => set({ createUpdateError: error }),
  setFetchError: (error) => set({ fetchError: error }),
  setIsAddEntityOpen: (isOpen) => set({ isAddEntityOpen: isOpen, ...(isOpen && { createUpdateError: { status: false, message: '' } }) }),

  getDropdownOptions: async (service, entity) => {
    try {
      logger.info(`Fetching dropdown options for ${entity}`);
      const { data } = await service.getMetadata(entity);
      const filtered: DropdownOptions = { ...data };
      
      // Only filter and add properties if they exist in the response
      if (data.profession_choices !== undefined) {
        filtered.profession_choices = filterProfessionChoices(data.profession_choices);
      }
      if (data.timezone_choices !== undefined) {
        filtered.timezone_choices = filterTimezoneChoices(data.timezone_choices);
      }
      
      set({ dropdownOptions: filtered });
      return filtered;
    } catch (error) {
      logger.error('Error fetching dropdown options:', error);
      return {};
    }
  },

  fetchEntities: async (service, entity, params = {}) => {
    set({ isLoading: true, fetchError: { status: false, message: '' } });
    try {
      const { queryState, pagination } = get();
      const requestParams = {
        search: queryState.search,
        filters: queryState.filters,
        ordering: queryState.ordering,
        rowsPerPage: pagination?.page_size,
        ...params
      };

      logger.info(`Fetching ${entity} entities with params:`, requestParams);
      const { status, data } = await service.fetchEntities(entity, requestParams);
      logger.info(`Successfully fetched ${entity} entities. Count: ${data.results?.length || 0}`);
      set({ 
        items: data.results || [], 
        pagination: data.pagination, 
        isLoading: false,
        fetchError: { status: false, message: '' }
      });
      return { status, data };
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, 'Failed to load data. Please try again.');
      logger.error(`Error fetching ${entity} entities:`, err);
      set({ 
        items: [], 
        isLoading: false,
        fetchError: { status: true, message: errorMessage }
      });
      return { status: 500, data: {} };
    }
  },

  saveEntity: async (service, entity, item, create = false) => {
    try {
      logger.info(`${create ? 'Creating' : 'Updating'} ${entity} entity`, { id: item?.id });
      const pEntity = await service.saveEntity(entity, item, create);

      if (!pEntity) {
        throw new Error('Failed to save item');
      }

      const { status, data } = pEntity;
      logger.info('Item saved successfully. Server Response:', status, data);
      
      // Refresh list after save
      await get().fetchEntities(service, entity, { page: 0 });
      
      // Reset error state and close modal/drawer if needed
      set({ createUpdateError: { status: false, message: '' } });

      if (create) {
        set({ isAddEntityOpen: false });
      } else {
        // For updates, we want to close the details card
        set({ selectedItem: null });
      }

      return { success: true, data };
    } catch (err: unknown) {
      const error = err as ApiError;
      // Special case: 409 = duplicate email
      const errorMessage = error?.response?.status === 409
        ? 'DUPLICATE_EMAIL'
        : error?.errors || extractErrorMessage(err, 'Failed to save. Please try again.');

      set({ createUpdateError: { status: true, message: errorMessage } });
      logger.error('onSaveItem', err);
      return { success: false };
    }
  },

  deleteEntity: async (service, entity, id) => {
    try {
      logger.info(`Deactivating ${entity} with id: ${id}`);
      await service.deleteEntity(entity, id);
      
      // Refresh list after delete
      await get().fetchEntities(service, entity, { page: 0 });
      
      logger.info(`Successfully deactivated ${entity} with id: ${id}`);
      set({ selectedItem: null, createUpdateError: { status: false, message: '' } });
      
      return true;
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, 'Failed to deactivate. Please try again.');
      logger.error(`Error deactivating ${entity} with id: ${id}`, err);
      set({ createUpdateError: { status: true, message: errorMessage } });
      return false;
    }
  },

  resetStore: () => {
    set({ ...initialState, queryState: { ...initialQueryState }, dropdownOptions: null, fetchError: { status: false, message: '' } });
  }
}));
